import React, { useState, useEffect, useRef } from "react";
import { FaUser, FaMapMarkerAlt } from "react-icons/fa";

const ScrollingUsers = ({ newUsers }) => {
  const [translateX, setTranslateX] = useState(0);
  const animationRef = useRef(null);
  const dragData = useRef({
    isDragging: false,
    startX: 0,
    lastX: 0,
    velocity: 0,
  });

  const cardWidth = 110 + 32; // min-w-[110px] + space-x-8 approx
  const totalCards = newUsers.length * 2;
  const totalWidth = cardWidth * totalCards;

  const friction = 0; // slows velocity on inertia
  const baseSpeed = 1.5; // auto-scroll base speed

  // Helper to wrap translateX between 0 and -totalWidth/2
  const wrapTranslateX = (value) => {
    if (value > 0) return value - totalWidth / 2;
    if (value < -totalWidth / 2) return value + totalWidth / 2;
    return value;
  };

  // Animation loop that handles both auto-scroll and inertia
  const step = () => {
    if (!dragData.current.isDragging) {
      // If dragging stopped, apply velocity inertia
      let velocity = dragData.current.velocity;

      if (Math.abs(velocity) < 0.1) {
        // If velocity is near zero, fallback to slow auto-scroll
        velocity = -baseSpeed;
      } else {
        // Apply friction to velocity
        velocity *= friction;
      }

      dragData.current.velocity = velocity;

      setTranslateX((prev) => {
        let next = prev + velocity;
        return wrapTranslateX(next);
      });
    }

    animationRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    animationRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationRef.current);
  }, [newUsers]);

  const onDragStart = (e) => {
    cancelAnimationFrame(animationRef.current);
    dragData.current.isDragging = true;
    dragData.current.velocity = 0;

    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    dragData.current.startX = clientX;
    dragData.current.lastX = clientX;
  };

  const onDragMove = (e) => {
    if (!dragData.current.isDragging) return;

    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragData.current.lastX;

    // Update translateX immediately on drag move
    setTranslateX((prev) => {
      let next = prev + deltaX;
      return wrapTranslateX(next);
    });

    // Calculate velocity as weighted average for smoothness
    dragData.current.velocity = dragData.current.velocity * 0.8 + deltaX * 0.2;

    dragData.current.lastX = clientX;
  };

  const onDragEnd = () => {
    dragData.current.isDragging = false;
    animationRef.current = requestAnimationFrame(step);
  };

  const formatLocation = (location) => {
    if (!location) return "";

    // Remove "All " if present
    const cleaned = location.replace(/^All\s+/i, "");

    // Get everything before first comma
    return cleaned.split(",")[0].trim();
  };

  return (
    <>
      <p className="text-gray-900 text-xl font-semibold mt-6 mb-6 text-center tracking-wide">
        Welcome Our New Users
      </p>
      <div
        className="overflow-hidden mt-2 mb-8"
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={() => dragData.current.isDragging && onDragEnd()}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
        style={{ cursor: dragData.current.isDragging ? "grabbing" : "grab" }}
      >
        <div
          className="flex space-x-8 select-none"
          style={{
            transform: `translateX(${translateX}px)`,
            willChange: "transform",
            userSelect: "none",
          }}
        >
          {[...newUsers, ...newUsers].map((user, index) => (
            <div
              key={index}
              className="flex flex-col items-center bg-gradient-to-tr from-cyan-400 via-blue-400 to-indigo-500 text-gray-900 px-6 py-4 rounded-3xl text-center shadow-lg shadow-pink-400/40 transition-transform transform hover:scale-110 hover:shadow-2xl cursor-pointer min-w-[110px]"
              title={user.name}
            >
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.name}
                  className="w-20 h-20 rounded-full object-cover mb-3 border-4 border-white"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-blue-800 flex items-center justify-center mb-3 border-4 border-white">
                  <FaUser className="w-9 h-9 text-white" />
                </div>
              )}
              <span
                className="text-sm font-medium truncate max-w-[90px] text-white drop-shadow-sm hover:text-yellow-300"
                style={{ wordBreak: "break-word" }}
                title={user.name} // show full name on hover
              >
                {user.name}
              </span>
              {user.location && (
                <span
                  className="text-xs text-white/80 mt-1 truncate max-w-[90px] flex items-center gap-1"
                  title={user.location}
                >
                 <FaMapMarkerAlt className="text-red-600 text-[11px] drop-shadow-sm" />
                  {formatLocation(user.location)}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ScrollingUsers;
